import {
  EditorContextType,
  useEditorContext,
  useTheme
} from '@graphiql/react';
import { Storage } from '@graphiql/toolkit';
import {
  GraphiQLInterface,
  GraphiQLProvider
} from 'graphiql';
import {
  buildSchema,
  GraphQLSchema
} from 'graphql';
import {
  FC,
  useEffect,
  useMemo,
  useState
} from 'react';
import { defaultUserSettings } from '../../const';
import { useServices } from '../../hooks';
import { UserService } from '../../services';
import { UserSettingsTheme } from '../../types';

type SchemaParseResult = (
  {
    error?: never;
    schema: GraphQLSchema | undefined;
  } | {
    error: string;
    schema?: never;
  }
);

function getGraphiqlKeys() {
  return Object.keys(localStorage).filter((key) => {
    return key.startsWith('graphiql:');
  });
}

function parseSchema(
  schemaString: string | null
): SchemaParseResult {
  if (!schemaString) {
    return { schema: undefined };
  }

  try {
    return { schema: buildSchema(schemaString) };
  } catch (error) {
    if (error instanceof Error) {
      return { error: error.message };
    } else if (typeof error === 'string') {
      return { error };
    }

    return { error: 'An unknown parsing error occurred.' };
  }
}

const tabStateKey = 'graphiql:tabState';

export type GraphQlWrapperProps = (
  GraphQlInnerProps
  & {
    onSchemaValidate?: (isValid: boolean) => void;
    schema?: string;
    url: string;
  }
);

/** GraphQL wrapper that handles schema parsing. */
export const GraphQlWrapper: FC<GraphQlWrapperProps> = ({
  onSchemaValidate,
  schema: schemaString,
  url,
  ...innerArgs
}) => {
  // Force editor to only have one tab.
  localStorage.removeItem(tabStateKey);
  const {
    error,
    schema
  } = useMemo(() => {
    const result = parseSchema(schemaString ?? null);
    onSchemaValidate?.(!result.error);
    return result;
  }, [schemaString]);
  const { userService } = useServices();
  const { theme, setTheme } = useTheme();
  const [localTheme, setLocalTheme] = useState(userService.userSettings.theme);

  useEffect(() => {
    if (theme !== localTheme) {
      setTheme(localTheme);
    }
    if (userService.userSettings.theme !== localTheme) {
      userService.userSettings = {
        ...userService.userSettings,
        theme: localTheme
      };
    }
  }, [localTheme]);

  /** Custom storage solution to keep its theme in sync with user settings. */
  const storage = useMemo<Storage>(() => {
    return {
      get length() {
        return getGraphiqlKeys().length;
      },
      clear: () => {
        getGraphiqlKeys().forEach((key) => {
          localStorage.removeItem(key);
        });
        setLocalTheme(defaultUserSettings.theme);
      },
      getItem(key) {
        return localStorage.getItem(key);
      },
      removeItem: (key) => {
        localStorage.removeItem(key);
        if (key === UserService.graphiqlThemeKey) {
          setLocalTheme(UserSettingsTheme.system);
        }
      },
      setItem: (key, value) => {
        localStorage.setItem(key, value);
        if (key === UserService.graphiqlThemeKey) {
          setLocalTheme(value as UserSettingsTheme);
        }
      }
    };
  }, []);

  if (error) {
    return (
      <label>
        The schema file content cannot be parsed:
        <br />
        {error}
      </label>
    );
  }

  return (
    <GraphiQLProvider
      defaultTabs={[{ query: null }]}
      fetcher={async (graphQlParams) => {
        return (await fetch('/graphiql', {
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            params: graphQlParams ?? {},
            url
          })
        })).json();
      }}
      storage={storage}
      schema={schema}
    >
      <GraphQlInner {...innerArgs} />
    </GraphiQLProvider>
  );
};

type GraphQlInnerProps = {
  onContextChange?: (context: EditorContextType | null) => void;
  readOnly?: boolean;
};

/** Inner component to access contexts created by @see GraphiQLProvider. */
const GraphQlInner: FC<GraphQlInnerProps> = ({
  onContextChange,
  readOnly
}) => {
  const editorContext = useEditorContext();

  useEffect(() => {
    onContextChange?.(editorContext);
  }, [editorContext]);

  useEffect(() => {
    return () => {
      $('.CodeMirror-hints').remove();
      $('.CodeMirror-lint-tooltip').remove();
      $('.CodeMirror-info').remove();
    };
  });

  return (
    <GraphiQLInterface
      readOnly={readOnly}
    />
  );
};
